import { ComponentChild } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'
import type { TeaserCardProps } from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { GroupHeader } from '@bonniernews/dn-design-system-web/components/group-header/group-header.tsx'

interface TeaserPackageProps extends SharedProps, Pick<TeaserCardProps, 'theme' | 'areaType'> {
  areaType?: 'main' | 'bauta'
  vignetteText?: string
  vignetteTargetUrl?: string
  vignetteIcon?: ComponentChild
  teasers?: ComponentChild
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-package](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-package)
 * - Storybook: [TeaserPackage](https://designsystem.dn.se/?path=/docs/section-teaserpackage--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 *
 * `@use '@bonniernews/dn-design-system-web/assets/teaser/teaser.forward.scss';`
 *
 * This will include the styles for the teaser package and all teasers.
 *
 * If you __only__ want to include the styles for the teaser package, you can do so by importing only teaser-package.scss:
 *
 * `@use '@bonniernews/dn-design-system-web/components/teaser-package/teaser-package.scss';`
 */
export const TeaserPackage = ({
  vignetteText,
  vignetteTargetUrl,
  vignetteIcon,
  teasers,
  theme,
  areaType,
  classNames,
  attributes = {},
}: TeaserPackageProps) => {
  const componentClassName = 'ds-teaser-package'
  const classes = formatClassString([
    componentClassName,
    areaType === 'bauta' && `${componentClassName}--${areaType}`,
    areaType === 'bauta' && 'ds-dark',
    !!theme && `ds-theme--${theme}`,
    classNames,
  ])

  return (
    <>
      <div className={classes} {...attributes}>
        {!!vignetteText && (
          <GroupHeader
            title={vignetteText ? vignetteText : ''}
            href={vignetteTargetUrl}
            variant={areaType === 'bauta' ? 'bauta' : undefined}
            media={vignetteIcon}
          />
        )}
        {teasers}
      </div>
    </>
  )
}
